// filepath: apps/web/src/features/admin/fieldVisibility/useRoleFieldVisibility.ts
//
// FR-UI-ADM-4 — TanStack Query hook for the admin viewer.
//
// Mirrors apps/web/src/features/admin/queries.ts:useRoleList /
// useRolePermissions / usePermissionsMatrix in shape and pattern.
// All hooks include `tenantSlug` as the 2nd segment of the cache
// key (INV-1) — switching tenants does NOT leak the prior tenant's
// classification.
//
// The hook reads from a new endpoint
//   GET /api/admin/role-field-visibility/{roleName}/{entityType}
// (see internal/kernel/role_field_visibility_handler.go). The data
// is fully server-resolved; the SPA never evaluates formulas or
// classification logic (FR-UI-NFR-2 — no client-side eval).

import { useQuery } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { adminKeys } from "@/features/admin/queries";
import { apiFetch } from "@/api";
import type {
  RoleFieldVisibilityError,
  RoleFieldVisibilityResponse,
} from "./types";

// ISS-377 round 2.1 (R2-B2): the raw `fetch('/api/...')` was a
// static-guard regression (no `Authorization: Bearer <access_token>`,
// the Go server's `internal/auth.AuthMiddleware` requires the
// bearer at `auth.go:51-62`). Routed through `apiFetch` with the
// OIDC auto-bearer accessor; the static-guard forbidlist
// (`apps/web/src/test/noRawApiFetchForbidlist.ts`) now requires
// every /api call in apps/web/src/** to go through this client.

/**
 * useRoleFieldVisibility — fetches the per-field classification map
 * for (roleName, entityType) on the resolved tenant.
 *
 * Cache key (adminKeys.fieldVisibility):
 *   ["admin", tenantSlug, "field-visibility", roleName, entityType]
 *
 * The key includes tenantSlug as the 2nd segment per INV-1; tests
 * verify the cache key shape matches adminKeys.matrix's structure.
 *
 * The hook returns TanStack Query's standard UseQueryResult
 * shape; the page maps `error` to one of the 5 FR-UI-CORE-10
 * error states.
 */
export function useRoleFieldVisibility(roleName: string, entityType: string) {
  const { tenantSlug } = useTenant();
  return useQuery<RoleFieldVisibilityResponse, ApiError>({
    queryKey: adminKeys.fieldVisibility(tenantSlug, roleName, entityType),
    queryFn: async () => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch (auto-bearer
      // via the OIDC accessor + 401 silent-refresh chain).
      const r = await apiFetch(
        `/api/admin/role-field-visibility/${encodeURIComponent(roleName)}/${encodeURIComponent(entityType)}`,
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as RoleFieldVisibilityResponse;
    },
    // The hook is only meaningful when both inputs are populated.
    // Pre-loading is gated by the URL param contract (the route
    // shell parses entityType from the URL).
    enabled: Boolean(roleName) && Boolean(entityType),
  });
}

/**
 * classifyRoleFieldVisibilityError — maps the raw fetch error
 * (typically a thrown ApiError) to the typed envelope the page
 * consumes. Mirrors `classifyMatrixError` in
 * PermissionsMatrixPage.tsx.
 */
export function classifyRoleFieldVisibilityError(
  err: Error | ApiError | unknown,
): RoleFieldVisibilityError {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return {
        kind: "permission_denied",
        message: err.message,
        retriable: false,
      };
    }
    if (err.status === 410 || err.status === 404) {
      return {
        kind: "stale_version",
        message: err.message,
        retriable: true,
      };
    }
    if (err.status === 429) {
      return {
        kind: "rate_limit",
        message: err.message,
        retriable: true,
      };
    }
    if (err.status === 0) {
      return {
        kind: "network",
        message: err.message,
        retriable: true,
      };
    }
  }
  return {
    kind: "fetch_failure",
    message: err instanceof Error ? err.message : String(err),
    retriable: true,
  };
}
